import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, Download, DownloadCloud, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { formatBusinessDate, formatTimestamp } from "@/utils/datetime";
import { PHOTO_ACCEPT, uploadPhotosToProject } from "@/utils/photoUploads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const gpsLabel = (photo) => {
  if (photo.gps_latitude == null || photo.gps_longitude == null) return "GPS not in EXIF";
  const lat = Number(photo.gps_latitude).toFixed(5);
  const lng = Number(photo.gps_longitude).toFixed(5);
  return `${lat}, ${lng}`;
};

// Compact Photos pill for the Project Detail header. Collapsed by default;
// tapping expands an inline panel below with the grid, upload, and download.
export default function ProjectPhotosPanel({ projectId, defaultOpen = false }) {
  const { isFeatureEnabled, hasPermission } = useAuth();
  const enabled = isFeatureEnabled("project_photos");
  const canRead = hasPermission("project_photos:read");
  const canManage = hasPermission("project_photos:manage");
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(defaultOpen);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const inputRef = useRef(null);

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setCheckedIds((prev) => {
      if (prev.size === photos.length) return new Set();
      return new Set(photos.map((p) => p.id));
    });
  };

  const downloadIds = async (idsIterable, isAll) => {
    const ids = Array.from(idsIterable);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/projects/${projectId}/photos/bulk-zip`,
        { photo_ids: ids },
        { responseType: "blob" },
      );
      const missing = Number(res.headers?.["x-missing-count"] || 0);
      const included = Number(res.headers?.["x-included-count"] || 0);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `project-${projectId}-photos.zip`;
      a.click();
      URL.revokeObjectURL(url);
      if (!isAll) setCheckedIds(new Set());
      if (missing > 0) {
        toast.warning(
          `${included} photo${included === 1 ? "" : "s"} downloaded — ${missing} skipped (storage lookup failed).`,
        );
      }
    } catch (error) {
      let detail = "ZIP download failed.";
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          const asText = await body.text();
          const parsed = JSON.parse(asText);
          if (parsed?.detail) detail = parsed.detail;
        } catch {
          // fall through with the generic message
        }
      } else if (body?.detail) {
        detail = body.detail;
      }
      toast.error(detail);
    }
  };

  const downloadZip = () => downloadIds(checkedIds, false);
  const downloadAll = () => downloadIds(photos.map((p) => p.id), true);

  const loadCount = useCallback(async () => {
    if (!enabled || !canRead) return;
    try {
      const res = await axios.get(`${API}/projects/${projectId}/photos/count`);
      setCount(res.data.count || 0);
    } catch (error) {
      // Silently ignore — the pill just won't show a number.
    }
  }, [enabled, canRead, projectId]);

  const loadList = useCallback(async () => {
    if (!enabled || !canRead) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/photos`);
      setPhotos(res.data.photos || []);
      setCount((res.data.photos || []).length);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load photos.");
    } finally {
      setLoading(false);
    }
  }, [enabled, canRead, projectId]);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(files.map((f) => ({ name: f.name, progress: 0 })));
    try {
      await uploadPhotosToProject(projectId, files, (index, progress) => {
        setUploadProgress((prev) => {
          if (!prev[index]) return prev;
          const next = prev.slice();
          next[index] = { ...next[index], progress };
          return next;
        });
      });
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}.`);
      await loadList();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      setUploadProgress([]);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (photo) => {
    if (!window.confirm(`Remove ${photo.original_filename}? It stays recoverable for 30 days.`)) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/photos/${photo.id}`);
      toast.success("Photo removed.");
      setSelected(null);
      await loadList();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!enabled || !canRead) return null;

  return (
    <div className="project-photos" data-testid="project-photos-root">
      <button
        className="table-edit-button"
        data-testid="project-photos-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
        type="button"
      >
        <Camera size={14} />
        Photos ({count})
      </button>

      {open && (
        <section
          data-testid="project-photos-panel"
          style={{
            marginTop: "0.65rem",
            padding: "0.75rem",
            border: "1px solid #e2e5ea",
            borderRadius: "8px",
            background: "#fafbfc",
          }}
        >
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.6rem" }}>
            {canManage && (
              <>
                <input
                  accept="image/*"
                  data-testid="project-photos-input"
                  multiple
                  onChange={upload}
                  ref={inputRef}
                  style={{ display: "none" }}
                  type="file"
                />
                <button
                  className="table-edit-button"
                  data-testid="project-photos-upload"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                  type="button"
                >
                  <Upload size={13} /> {uploading ? "Uploading…" : "Upload"}
                </button>
              </>
            )}
            {photos.length > 0 && (
              <>
                <button
                  className="table-edit-button"
                  data-testid="project-photos-select-all"
                  onClick={toggleSelectAll}
                  type="button"
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                >
                  {checkedIds.size === photos.length ? "Clear selection" : `Select all (${photos.length})`}
                </button>
                <button
                  className="table-edit-button"
                  data-testid="project-photos-download-all"
                  onClick={downloadAll}
                  type="button"
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                >
                  <DownloadCloud size={13} /> Download all ({photos.length})
                </button>
              </>
            )}
            {checkedIds.size > 0 && checkedIds.size !== photos.length && (
              <button
                className="table-edit-button"
                data-testid="project-photos-download-zip"
                onClick={downloadZip}
                type="button"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              >
                <DownloadCloud size={13} /> Download selected ({checkedIds.size})
              </button>
            )}
            <button
              data-testid="project-photos-close"
              onClick={() => setOpen(false)}
              style={{ marginLeft: "auto" }}
              type="button"
            >
              Close
            </button>
          </div>

          {loading ? (
            <p data-testid="project-photos-loading">Loading…</p>
          ) : uploadProgress.length > 0 ? (
            <ul
              data-testid="project-photos-upload-progress"
              style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "0.35rem" }}
            >
              {uploadProgress.map((item, index) => (
                <li
                  data-testid={`project-photos-upload-progress-${index}`}
                  key={`${item.name}-${index}`}
                  style={{ fontSize: "0.8rem", color: "#374151" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "0.5rem" }}>
                    <span
                      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={item.name}
                    >
                      {item.name}
                    </span>
                    <span>{Math.round(item.progress * 100)}%</span>
                  </div>
                  <div
                    style={{
                      background: "#e5e7eb",
                      height: 4,
                      borderRadius: 2,
                      overflow: "hidden",
                      marginTop: 2,
                    }}
                  >
                    <div
                      style={{
                        background: item.progress >= 1 ? "#16a34a" : "#2563eb",
                        height: "100%",
                        width: `${Math.min(100, item.progress * 100)}%`,
                        transition: "width 120ms linear",
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : photos.length === 0 ? (
            <p data-testid="project-photos-empty" style={{ color: "#64748b" }}>
              No photos yet.
            </p>
          ) : (
            <div
              data-testid="project-photos-grid"
              style={{
                display: "grid",
                gap: "0.5rem",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              }}
            >
              {photos.map((photo) => {
                const isChecked = checkedIds.has(photo.id);
                return (
                  <div
                    key={photo.id}
                    data-testid={`project-photo-tile-${photo.id}`}
                    style={{
                      position: "relative",
                      border: isChecked ? "2px solid #2563eb" : "1px solid #d1d5db",
                      borderRadius: "6px",
                      overflow: "hidden",
                      background: "#fff",
                      aspectRatio: "1 / 1",
                    }}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${photo.original_filename}`}
                      data-testid={`project-photo-check-${photo.id}`}
                      checked={isChecked}
                      onChange={() => toggleChecked(photo.id)}
                      style={{
                        position: "absolute",
                        top: 6,
                        left: 6,
                        zIndex: 2,
                        width: 18,
                        height: 18,
                        cursor: "pointer",
                      }}
                    />
                    <button
                      data-testid={`project-photo-thumb-${photo.id}`}
                      onClick={() => setSelected(photo)}
                      style={{
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        width: "100%",
                        height: "100%",
                      }}
                      title={photo.original_filename}
                      type="button"
                    >
                      <img
                        alt={photo.original_filename}
                        src={photo.thumbnail_url}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {selected && (
        <div
          data-testid="project-photo-detail"
          onClick={() => setSelected(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 60,
            padding: "1rem",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "8px",
              maxWidth: "540px",
              width: "100%",
              padding: "1rem",
            }}
          >
            <h3 style={{ marginTop: 0 }}>{selected.original_filename}</h3>
            <img
              alt={selected.original_filename}
              src={selected.thumbnail_url}
              style={{ maxWidth: "100%", borderRadius: "6px" }}
            />
            <dl style={{ fontSize: "0.85rem", marginTop: "0.75rem" }}>
              <div><strong>Captured:</strong> {selected.captured_at ? formatTimestamp(selected.captured_at) : "—"}</div>
              <div><strong>Uploaded:</strong> {formatTimestamp(selected.uploaded_at)} by {selected.uploaded_by_name_snapshot}</div>
              <div><strong>GPS:</strong> {gpsLabel(selected)}</div>
              <div><strong>Size:</strong> {(selected.byte_size / 1024).toFixed(1)} KB · {selected.content_type}</div>
              {selected.exif_summary?.Make && (
                <div>
                  <strong>Camera:</strong> {selected.exif_summary.Make} {selected.exif_summary.Model || ""}
                </div>
              )}
            </dl>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
              <a
                className="table-edit-button"
                data-testid="project-photo-download"
                download={selected.original_filename}
                href={`${API}/projects/${projectId}/photos/${selected.id}/download`}
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
              >
                <Download size={13} /> Download original
              </a>
              {canManage && (
                <button
                  className="table-edit-button"
                  data-testid="project-photo-delete"
                  onClick={() => remove(selected)}
                  style={{ color: "#dc2626" }}
                  type="button"
                >
                  <Trash2 size={13} /> Remove
                </button>
              )}
              <button
                data-testid="project-photo-close-detail"
                onClick={() => setSelected(null)}
                style={{ marginLeft: "auto" }}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
